
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  description: 'Plaid Exchange Data Aggregation Reference',
  title: 'Plaid Exchange Reference',
  section: 'Aggregation'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const AggregatorSchema = makeShortcode("AggregatorSchema");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Aggregation API Reference' subtitle={metadata.description} mdxType="Header" />
    <p>{`This API enables Plaid to make recurrent, user-absent requests in order to maintain a current and consistent view of a user’s permissioned accounts.`}</p>
    <h4 {...{
      "id": "get-account-and-identity-endpoint"
    }}>{`Get Account and Identity Endpoint`}</h4>
    <p><inlineCode parentName="p">{`GET /users/{user_id}`}</inlineCode></p>
    <p>{`Provide user and account information for a given user ID. This endpoint contains the information necessary for the partner to support the Identity and Auth products.`}</p>
    <h5 {...{
      "id": "responses"
    }}>{`Responses`}</h5>
    <h6 {...{
      "id": "200-ok"
    }}><inlineCode parentName="h6">{`200 OK`}</inlineCode></h6>
    <p>{`The request is authorized.`}</p>
    <h6 {...{
      "id": "useraccountinforesponse"
    }}><inlineCode parentName="h6">{`UserAccountInfoResponse`}</inlineCode></h6>
    <AggregatorSchema type='model' model='UserAccountInfoResponse' mdxType="AggregatorSchema" />
    <Callout title="Privacy Implications of Per-User Request Model" mdxType="Callout">
      <p>{`Plaid’s privacy guarantee to end users is that it will never share per-account data, including knowledge of the existence of individual accounts, with applications unless the user has affirmatively indicated those accounts and applications should be linked. Plaid also will also minimize the storage details of accounts retrieved through this API which are not linked to any applications.`}</p>
      <p>{`At times, it is necessary to communicate information about accounts not connected to any Plaid-powered applications, primarily for the purpose of enabling sensible UX, e.g. when drawing account selection screens. To ease implementation, this API operates at a per-user granularity and not a per-account granularity, because Plaid assumes the responsibility of providing a privacy-conscious view of the user’s accounts to each application.`}</p>
      <p>{`However, Plaid provides the `}<a parentName="p" {...{
          "href": "/plaid-exchange/docs/reference/data-definitions/#notionalaccount"
        }}><inlineCode parentName="a">{`NotionalAccount`}</inlineCode></a>{` and `}<a parentName="p" {...{
          "href": "/plaid-exchange/docs/reference/data-definitions/#basicidentity"
        }}><inlineCode parentName="a">{`BasicIdentity`}</inlineCode></a>{` models to enable implementers, if desired, to mask the existence of identities and accounts it knows are not linked to any Plaid applications. The partner is expected to use the `}<a parentName="p" {...{
          "href": "/plaid-exchange/docs/authentication/"
        }}>{`Authorization API`}</a>{` to ensure its access policy is synchronized with that of Plaid’s, i.e. both Plaid and the partner see the same items. Failure to do so will result in an inconsistent state and degraded end-user experiences.`}</p>
    </Callout>
    <h6 {...{
      "id": "304-not-modified"
    }}><inlineCode parentName="h6">{`304 Not Modified`}</inlineCode></h6>
    <p>{`If present, Plaid will consume the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/ETag"
      }}><inlineCode parentName="a">{`ETag`}</inlineCode></a>{` header, and then present the most-recently seen `}<inlineCode parentName="p">{`ETag`}</inlineCode>{` using the `}<inlineCode parentName="p">{`If-None-Match`}</inlineCode>{` header on subsequent requests. If there has been no activity on, and no change to, the customer’s account, the partner may return `}<inlineCode parentName="p">{`304 Not Modified`}</inlineCode>{` with an empty HTTP body. Plaid will end the session and send no more requests until the next scheduled update.`}</p>
    <p>{`It is recommended to generate `}<inlineCode parentName="p">{`ETags`}</inlineCode>{` by concatenating and hashing the `}<inlineCode parentName="p">{`account_ids`}</inlineCode>{` and `}<inlineCode parentName="p">{`last_activity_at`}</inlineCode>{` timestamps for all accounts present in the response. If any identity data has changed, the `}<inlineCode parentName="p">{`ETag`}</inlineCode>{` should always be new.`}</p>
    <h4 {...{
      "id": "get-transactions-endpoint"
    }}>{`Get Transactions Endpoint`}</h4>
    <p><inlineCode parentName="p">{`GET /users/{user_id}/transactions`}</inlineCode></p>
    <p>{`Provides a query interface for a user’s transactions across all accounts, optionally filtered by posting date.`}</p>
    <h6 {...{
      "id": "parameters"
    }}>{`Parameters`}</h6>
    <AggregatorSchema method='GET' route='/users/{user_id}/transactions' type='request' mdxType="AggregatorSchema" />
    <Callout title="Note" mdxType="Callout">
      <p>{`When considering whether a pending transaction (one which has not yet posted)
should be included in a `}<inlineCode parentName="p">{`TransactionsResponse`}</inlineCode>{`, evaluate whether the transaction
date (`}<inlineCode parentName="p">{`transacted_at`}</inlineCode>{`) falls within the range.`}</p>
    </Callout>
    <h5 {...{
      "id": "responses-1"
    }}>{`Responses`}</h5>
    <h6 {...{
      "id": "200-ok-1"
    }}><inlineCode parentName="h6">{`200 OK`}</inlineCode></h6>
    <p>{`The request was authorized and well-formed.`}</p>
    <h6 {...{
      "id": "transactionsresponse"
    }}><inlineCode parentName="h6">{`TransactionsResponse`}</inlineCode></h6>
    <AggregatorSchema type='model' model='TransactionsResponse' mdxType="AggregatorSchema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-account-and-identity-endpoint",
        level: 4,
        title: "Get Account and Identity Endpoint",
      },
{
        id: "get-transactions-endpoint",
        level: 4,
        title: "Get Transactions Endpoint",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
